import {
  leadSchema,
  leadStageNames,
  leadStages,
  type LeadDetail,
  type LeadInput,
  type LeadMove,
  type LeadStage,
  type Me,
} from '@smarton-pbx/shared';
import { parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js';
import { BookUser, Pencil, Phone, Plus, Trash2, UserPlus, X } from 'lucide-react';
import { type FormEvent, useEffect, useId, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../api';
import { Field, Loading, Notice } from '../../components/ui';
import { locale, t, tc } from '../../i18n';
import { formatNumber } from '../../lib/calls';
import { useLoad, useSubmit } from '../../lib/hooks';
import { CallTimeline } from '../contacts';
import { usePhone } from '../softphone';
import { formatDay, localDay, leadTitle, sourceSuggestions, StageDot } from './leads';

const when = (iso: string) => new Date(iso).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' });

/**
 * A lead's panel: call someone there, then say where they stand and when to
 * call next, with notes and every call with them. It stays open after the call, and
 * "Next lead" goes on down the column. A drawer beside the board, not a modal:
 * the call card (to hang up) and the board stay usable while it's open.
 */
export function LeadPanel({
  me,
  leadId,
  people,
  sources,
  nextAfter,
  onOpen,
  onClose,
  onChanged,
}: {
  me: Me;
  leadId: string | null;
  people: { id: string; name: string }[];
  /** Where the team's leads came from, to suggest. */
  sources: string[];
  nextAfter: (lead: { id: string; stage: LeadStage }) => string | null;
  onOpen: (id: string) => void;
  onClose: () => void;
  onChanged: () => void;
}) {
  const lead = useLoad(() => (leadId ? api.getLead(leadId) : Promise.resolve(null)), [leadId]);
  // "person": with a new person to fill in.
  const [editing, setEditing] = useState<boolean | 'person'>(false);
  // Notes being written, a next call's time being typed (null: as saved).
  const [notes, setNotes] = useState<string | null>(null);
  const [time, setTime] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const phone = usePhone();

  useEffect(() => {
    setEditing(false);
    setNotes(null);
    setTime(null);
    setError(undefined);
  }, [leadId]);

  // A call ended: it's on the lead now (and may have moved it).
  useEffect(() => {
    if (!leadId || phone.call?.state !== 'ended') return;
    const timer = setTimeout(lead.reload, 1500);
    return () => clearTimeout(timer);
  }, [leadId, phone.call?.state, lead.reload]);

  // Summaries still being written: look again in a while.
  const pending = lead.data?.calls.some((c) => c.summaryPending);
  useEffect(() => {
    if (!pending) return;
    const timer = setTimeout(lead.reload, 5000);
    return () => clearTimeout(timer);
  }, [pending, lead.data, lead.reload]);

  const l = lead.data?.id === leadId ? lead.data : null;

  async function change(work: () => Promise<LeadDetail>) {
    setBusy(true);
    setError(undefined);
    try {
      lead.setData(await work());
      onChanged();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return false;
    } finally {
      setBusy(false);
    }
  }

  const patch = (move: LeadMove) => l && void change(() => api.moveLead(l.id, move));

  /** The time typed, once they leave it: saving at each keystroke would save half a time. A time with no day is today's. */
  async function saveTime() {
    if (!l || time === null) return;
    const value = time || null;
    if (value !== l.followUpTime) {
      await change(() => api.moveLead(l.id, { followUpTime: value, ...(value && !l.followUpOn && { followUpOn: localDay() }) }));
    }
    setTime(null);
  }

  async function saveNotes() {
    if (!l || notes === null) return;
    if (await change(() => api.updateLead(l.id, { ...inputOf(l), notes }))) setNotes(null);
  }

  async function remove() {
    if (!l || !confirm(t('Delete {name}? Their calls stay.', { name: leadTitle(l) }))) return;
    try {
      await api.deleteLead(l.id);
      onChanged();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  // Escape closes it, unless they're typing (or have notes to save).
  const writing = editing || (notes !== null && notes !== l?.notes);
  useEffect(() => {
    if (!leadId) return;
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (event.key !== 'Escape' || writing || target?.closest('input, textarea, select, dialog')) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [leadId, writing, onClose]);

  const titleId = useId();
  if (!leadId) return null;
  const next = l ? nextAfter(l) : null;
  const owners = l?.ownerId && l.ownerName && !people.some((p) => p.id === l.ownerId) ? [...people, { id: l.ownerId, name: l.ownerName }] : people;
  // Calls with which person, when there are several to tell apart.
  const who = l && l.contacts.filter((c) => c.e164).length > 1 ? Object.fromEntries(l.contacts.flatMap((c) => (c.e164 && c.name ? [[c.e164, c.name]] : []))) : undefined;
  // On a call: room at the bottom, so nothing stays under the call card.
  const onCall = Boolean(phone.call && phone.call.state !== 'ended');
  return (
    <aside className={`lead-drawer ${onCall ? 'on-call' : ''}`} role="dialog" aria-labelledby={titleId} data-testid="lead-drawer">
      <div className="dialog-header">
        <h2 id={titleId}>{l ? leadTitle(l) : t('Lead')}</h2>
        <div className="row">
          {l && !editing && (
            <button type="button" className="secondary small" onClick={() => setEditing(true)} data-testid="edit-lead">
              <Pencil size={14} aria-hidden /> {t('Edit')}
            </button>
          )}
          <button type="button" className="ghost icon" onClick={onClose} aria-label={t('Close')} data-testid="close-lead">
            <X size={18} aria-hidden />
          </button>
        </div>
      </div>
      <div className="lead-drawer-scroll">
        {lead.error ? (
          <div className="dialog-body">
            <Notice kind="error">{lead.error}</Notice>
          </div>
        ) : !l ? (
          <div className="dialog-body">
            <Loading />
          </div>
        ) : editing ? (
          <LeadForm
            me={me}
            lead={l}
            people={owners}
            sources={sources}
            addPerson={editing === 'person'}
            onCancel={() => setEditing(false)}
            onSaved={(saved) => {
              lead.setData(saved);
              setEditing(false);
              onChanged();
            }}
          />
        ) : (
          <>
            <div className="dialog-body stack lead-panel" data-testid="lead-panel">
              <div className="lead-people" data-testid="lead-people">
                {l.contacts.length === 0 && <p className="subtle">{t('Nobody yet. Add the people you talk to there.')}</p>}
                {l.contacts.map((person) => (
                  <div key={person.id} className="lead-person-row" data-testid="lead-person">
                    <div className="lead-person-who">
                      {(person.name || person.position) && (
                        <div>
                          <strong>{person.name ?? person.position}</strong>
                          {person.name && person.position && <span className="subtle"> · {person.position}</span>}
                        </div>
                      )}
                      {(person.email || person.saved) && (
                        <div className="lead-person-links">
                          {person.email && <a href={`mailto:${person.email}`}>{person.email}</a>}
                          {person.saved && (
                            <Link to={`/contacts/${person.saved.id}`} className="plain-link">
                              <BookUser size={14} aria-hidden /> {person.saved.name}
                            </Link>
                          )}
                        </div>
                      )}
                    </div>
                    {person.e164 && (
                      <button
                        type="button"
                        className="small"
                        onClick={() => phone.dial(person.e164!)}
                        disabled={phone.status !== 'registered' || phone.busy}
                        aria-label={t('Call {name}', { name: person.name ?? formatNumber(person.e164) })}
                        data-testid="lead-call"
                      >
                        <Phone size={14} aria-hidden /> <span className="tabular">{formatNumber(person.e164)}</span>
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" className="ghost small" onClick={() => setEditing('person')} data-testid="add-person">
                  <UserPlus size={14} aria-hidden /> {tc('lead', 'Add person')}
                </button>
              </div>

              {l.talked && l.lastCall && <Notice>{t('You talked {when}. What came of it? Pick where they stand now.', { when: when(l.lastCall.at) })}</Notice>}

              <Field label={t('Where they stand')} group>
                <div className="stage-picker">
                  {leadStages.map((stage) => (
                    <button
                      key={stage}
                      type="button"
                      className={`stage-option stage-${stage} ${l.stage === stage ? 'active' : ''}`}
                      aria-pressed={l.stage === stage}
                      disabled={busy}
                      onClick={() => patch({ stage })}
                      data-testid={`stage-${stage}`}
                    >
                      <StageDot stage={stage} /> {t(leadStageNames[stage])}
                    </button>
                  ))}
                </div>
              </Field>

              <div className="form-grid">
                <Field label={t('Next call')} group>
                  <div className="row next-call">
                    <input
                      type="date"
                      value={l.followUpOn ?? ''}
                      onChange={(e) => patch({ followUpOn: e.target.value || null })}
                      aria-label={t('Next call')}
                      disabled={busy}
                      data-testid="lead-next-call"
                    />
                    <input
                      type="time"
                      value={time ?? l.followUpTime ?? ''}
                      onChange={(e) => setTime(e.target.value)}
                      onBlur={() => void saveTime()}
                      onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
                      aria-label={t('Time of the next call')}
                      title={t('At a time, if you agreed one')}
                      data-testid="lead-next-call-time"
                    />
                    {l.followUpOn && (
                      <button
                        type="button"
                        className="ghost icon"
                        aria-label={t('No next call')}
                        title={t('No next call')}
                        disabled={busy}
                        onClick={() => {
                          setTime(null);
                          patch({ followUpOn: null });
                        }}
                      >
                        <X size={16} aria-hidden />
                      </button>
                    )}
                    <span className="row next-call-days">
                      <button type="button" className="chip-button" disabled={busy} onClick={() => patch({ followUpOn: localDay(1) })}>
                        {t('Tomorrow')}
                      </button>
                      <button type="button" className="chip-button" disabled={busy} onClick={() => patch({ followUpOn: localDay(7) })}>
                        {t('In a week')}
                      </button>
                    </span>
                  </div>
                </Field>
                <Field label={t('Who works it')}>
                  <select value={l.ownerId ?? ''} disabled={busy} onChange={(e) => patch({ ownerId: e.target.value || null })} data-testid="lead-owner">
                    <option value="">{t('Nobody')}</option>
                    {owners.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.id === me.id ? t('{name} (you)', { name: p.name }) : p.name}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              <Field label={t('Notes')}>
                <textarea rows={5} value={notes ?? l.notes} onChange={(e) => setNotes(e.target.value)} data-testid="lead-notes" />
              </Field>
              {notes !== null && notes !== l.notes && (
                <div className="row">
                  <button type="button" onClick={() => void saveNotes()} disabled={busy} data-testid="save-notes">
                    {t('Save notes')}
                  </button>
                  <button type="button" className="ghost" onClick={() => setNotes(null)}>
                    {t('Undo')}
                  </button>
                </div>
              )}

              <dl className="lead-facts">
                {l.activity && (
                  <>
                    <dt>{t('What they do')}</dt>
                    <dd>{l.activity}</dd>
                  </>
                )}
                {l.topic && (
                  <>
                    <dt>{t('Interested in')}</dt>
                    <dd>{l.topic}</dd>
                  </>
                )}
                {l.source && (
                  <>
                    <dt>{t('Source')}</dt>
                    <dd>{l.source}</dd>
                  </>
                )}
                <dt>{t('Added')}</dt>
                <dd data-testid="lead-added">
                  {formatDay(localDay(0, new Date(l.createdAt)))}
                  {l.createdBy && <span className="subtle"> · {l.createdBy}</span>}
                </dd>
              </dl>

              <div className="stack" style={{ gap: '0.5rem' }}>
                <h3 className="lead-section">{t('Calls')}</h3>
                {l.calls.length === 0 ? (
                  <p className="subtle">{t('No calls with them yet.')}</p>
                ) : (
                  <CallTimeline calls={l.calls} {...(who && { who })} />
                )}
              </div>
              {error && <Notice kind="error">{error}</Notice>}
            </div>
            <div className="dialog-footer">
              {me.role !== 'AGENT' && (
                <button type="button" className="ghost icon" aria-label={t('Delete lead')} title={t('Delete lead')} onClick={() => void remove()}>
                  <Trash2 size={18} aria-hidden />
                </button>
              )}
              <span className="grow" />
              <button type="button" className="secondary" onClick={onClose}>
                {t('Close')}
              </button>
              {next && (
                <button type="button" onClick={() => onOpen(next)} data-testid="next-lead">
                  {t('Next lead')}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </aside>
  );
}

/** A lead as the form sends it back (notes and all). */
function inputOf(lead: LeadDetail): LeadInput {
  return {
    company: lead.company,
    contacts: lead.contacts.map(({ name, position, e164, email }) => ({ name, position, e164, email })),
    activity: lead.activity,
    topic: lead.topic,
    source: lead.source,
    notes: lead.notes,
    ownerId: lead.ownerId,
  };
}

type PersonRow = { key: number; name: string; position: string; number: string; email: string };

let rowKeys = 0;
const blankPerson = (): PersonRow => ({ key: ++rowKeys, name: '', position: '', number: '', email: '' });
const filledIn = (row: PersonRow) => Boolean(row.name.trim() || row.position.trim() || row.number.trim() || row.email.trim());

/**
 * A lead's details: the company, and the people there the team talks to, each
 * with their own number and email. Numbers as people write them; saved with
 * the country code.
 */
export function LeadForm({
  me,
  lead,
  people,
  sources,
  addPerson,
  onCancel,
  onSaved,
}: {
  me: Me;
  lead: LeadDetail | null;
  people: { id: string; name: string }[];
  /** Where the team's leads came from, to suggest. */
  sources: string[];
  /** Start with a new person to fill in. */
  addPerson?: boolean;
  onCancel: () => void;
  onSaved: (saved: LeadDetail) => void;
}) {
  const [company, setCompany] = useState(lead?.company ?? '');
  const [persons, setPersons] = useState<PersonRow[]>(() => {
    const rows = (lead?.contacts ?? []).map((c) => ({
      key: ++rowKeys,
      name: c.name ?? '',
      position: c.position ?? '',
      number: c.e164 ? formatNumber(c.e164) : '',
      email: c.email ?? '',
    }));
    return rows.length === 0 || addPerson ? [...rows, blankPerson()] : rows;
  });
  // The row whose name to type first (a person added).
  const [focusKey, setFocusKey] = useState<number | null>(() => (addPerson ? (persons[persons.length - 1]?.key ?? null) : null));
  const [activity, setActivity] = useState(lead?.activity ?? '');
  const [topic, setTopic] = useState(lead?.topic ?? '');
  const [source, setSource] = useState(lead?.source ?? '');
  const sourcesId = useId();
  const [notes, setNotes] = useState(lead?.notes ?? '');
  // Whoever adds a lead works it, unless they say otherwise.
  const [ownerId, setOwnerId] = useState(lead ? (lead.ownerId ?? '') : me.id);
  const form = useSubmit();
  const country = (me.region?.countryCode ?? 'LT') as CountryCode;

  const change = (key: number, field: keyof Omit<PersonRow, 'key'>, value: string) =>
    setPersons((rows) => rows.map((row) => (row.key === key ? { ...row, [field]: value } : row)));

  function add() {
    const row = blankPerson();
    setPersons((rows) => [...rows, row]);
    setFocusKey(row.key);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    // Rows left empty go; the rest keep their places, so errors land on the right row.
    const filled = persons.filter(filledIn);
    if (filled.length !== persons.length) setPersons(filled.length ? filled : [blankPerson()]);
    const input: LeadInput = {
      company: company || null,
      contacts: filled.map((row) => {
        const typed = row.number.trim();
        return {
          name: row.name || null,
          position: row.position || null,
          e164: typed ? (parsePhoneNumberFromString(typed, country)?.number ?? typed.replace(/[\s()-]/g, '')) : null,
          email: row.email || null,
        };
      }),
      activity: activity || null,
      topic: topic || null,
      source: source || null,
      notes,
      ownerId: ownerId || null,
    };
    const saved = await form.submit(leadSchema, input, (value) => (lead ? api.updateLead(lead.id, value) : api.createLead(value)));
    if (saved) onSaved(saved);
  }

  const fieldError = (i: number, field: string) => form.fields[`contacts.${i}.${field}`];
  return (
    <form onSubmit={submit} noValidate>
      <div className="dialog-body stack">
        <div className="form-grid">
          <Field label={t('Company')} error={form.fields.company}>
            <input value={company} onChange={(e) => setCompany(e.target.value)} autoFocus={!addPerson} placeholder="UAB Pavyzdys" data-testid="lead-form-company" />
          </Field>
          <Field label={t('What they do')} hint={t('Optional')}>
            <input value={activity} onChange={(e) => setActivity(e.target.value)} placeholder={t('Cafés and restaurants')} />
          </Field>
          <Field label={t('Interested in')} hint={t('Optional')}>
            <input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder={t('Accounting, POS')} />
          </Field>
        </div>

        <Field label={tc('lead', 'People')} group>
          <div className="stack lead-person-list">
            {persons.map((row, i) => (
              <div key={row.key} className="lead-person-fields" data-testid="lead-form-person">
                <div className="form-grid">
                  <Field label={t('Name')} error={fieldError(i, 'name')}>
                    <input
                      value={row.name}
                      onChange={(e) => change(row.key, 'name', e.target.value)}
                      autoFocus={row.key === focusKey}
                      placeholder="Jonas"
                      data-testid="lead-form-name"
                    />
                  </Field>
                  <Field label={t('Position')}>
                    <input
                      value={row.position}
                      onChange={(e) => change(row.key, 'position', e.target.value)}
                      placeholder={t('Director, accountant…')}
                      data-testid="lead-form-position"
                    />
                  </Field>
                  <Field label={t('Number')} error={fieldError(i, 'e164')}>
                    <input
                      value={row.number}
                      onChange={(e) => change(row.key, 'number', e.target.value)}
                      placeholder="+370 600 00000"
                      inputMode="tel"
                      className="tabular"
                      data-testid="lead-form-number"
                    />
                  </Field>
                  <Field label={t('Email')} error={fieldError(i, 'email')}>
                    <input type="email" value={row.email} onChange={(e) => change(row.key, 'email', e.target.value)} data-testid="lead-form-email" />
                  </Field>
                </div>
                <button
                  type="button"
                  className="ghost icon lead-person-remove"
                  aria-label={row.name ? t('Remove {name}', { name: row.name }) : t('Remove')}
                  title={t('Remove')}
                  onClick={() => setPersons((rows) => rows.filter((r) => r.key !== row.key))}
                  data-testid="lead-form-remove-person"
                >
                  <X size={16} aria-hidden />
                </button>
              </div>
            ))}
            <button type="button" className="ghost small" style={{ justifySelf: 'start' }} onClick={add} data-testid="lead-form-add-person">
              <Plus size={14} aria-hidden /> {tc('lead', 'Add person')}
            </button>
          </div>
        </Field>

        <div className="form-grid">
          <Field label={t('Who works it')} error={form.fields.ownerId}>
            <select value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
              <option value="">{t('Nobody')}</option>
              {people.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.id === me.id ? t('{name} (you)', { name: p.name }) : p.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('Source')} hint={t('Optional')}>
            <input
              value={source}
              onChange={(e) => setSource(e.target.value)}
              list={sourcesId}
              autoComplete="off"
              placeholder={t('Facebook, cold call…')}
              data-testid="lead-form-source"
            />
            <datalist id={sourcesId}>
              {sourceSuggestions(sources).map((suggestion) => (
                <option key={suggestion} value={suggestion} />
              ))}
            </datalist>
          </Field>
        </div>
        {!lead && (
          <Field label={t('Notes')}>
            <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
        )}
        {form.error && <Notice kind="error">{form.error}</Notice>}
      </div>
      <div className="dialog-footer">
        <button type="button" className="secondary" onClick={onCancel}>
          {t('Cancel')}
        </button>
        <button type="submit" disabled={form.busy} data-testid="lead-form-save">
          {t('Save')}
        </button>
      </div>
    </form>
  );
}
